import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Alert, Box, IconButton, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { ErrorState, PageContainer } from '@/shared/ui';

import { BASE_SCOPE, decodeScope, encodeScope } from '../../lib/priceGrid';
import type { GridScope } from '../../lib/priceGrid';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { BulkChangesPanel } from '../components/BulkChangesPanel';
import type { BulkChangesView } from '../components/BulkChangesPanel';
import { ChannelsPanel } from '../components/ChannelsPanel';
import { PriceApprovalsPanel } from '../components/PriceApprovalsPanel';
import { PriceAuditPanel } from '../components/PriceAuditPanel';
import { PriceGridPanel } from '../components/PriceGridPanel';
import { PriceListsPanel } from '../components/PriceListsPanel';
import type { PriceListsView } from '../components/PriceListsPanel';
import { PriceOperationsPanel } from '../components/PriceOperationsPanel';
import { PriceProtectionPanel } from '../components/PriceProtectionPanel';
import { PricingConfigPanel } from '../components/PricingConfigPanel';
import { PricingRulesPanel } from '../components/PricingRulesPanel';

type TabKey =
  | 'grid'
  | 'priceLists'
  | 'bulk'
  | 'rules'
  | 'config'
  | 'approvals'
  | 'protection'
  | 'audit'
  | 'channels'
  | 'operations';

// Tab order (design spec §5, plan-platform-002 WP4.3 — the fuller re-sequencing WP4.2 deferred):
// merchant frequency, not alphabetical/historical order. "Bulk changes" (`bulk`) is new, inserted
// right after "Price lists"; "Channels" is relabelled "Channels & groups" (key unchanged,
// copy-only — it now hosts customer-group management, `ChannelsPanel`); "Operations" (diagnostics
// console) moves to LAST, unchanged inside.
const TAB_KEYS: TabKey[] = [
  'grid',
  'priceLists',
  'bulk',
  'rules',
  'config',
  'approvals',
  'protection',
  'audit',
  'channels',
  'operations',
];
const PRICE_LISTS_VIEWS: PriceListsView[] = ['lists', 'coverage'];
const BULK_VIEWS: BulkChangesView[] = ['batches', 'markdown'];

/**
 * Price governance — rules, configuration, approval queue, protection policy and audit.
 *
 * `?tab=`/`?view=`/`?store=`/`?targetListCode=`/`?group=`/`?productId=`/`?batch=` are the
 * deep-link surface this page needs (design spec §5/§12.4) — read on mount, written with
 * `replace` on change, mirroring `ChannelStockPage`'s established URL-sync convention. `?group=`
 * seeds the grid's customer-group select, `?productId=` seeds the Approvals/Audit tabs' product
 * filter (the card drawer's own deep links, design spec §8.3/§8.7), and `?batch=` opens a batch
 * detail on the Bulk tab — each is a read-on-mount seed for state the panel still owns locally
 * (the same class as `?store=`/`?targetListCode=`), not a value lifted onto this page the way
 * `?scope=` is. Every param is cleared the moment its own tab is no longer active. Every OTHER
 * tab keeps its pre-existing in-page-only state.
 */
export function PricingPage() {
  const { t } = useTranslation('catalog');
  const { canPricingView, canPricingManage, canPricingAdmin, canPricingBulk } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'grid');
  const scopeParam = searchParams.get('scope');
  const [gridScope, setGridScope] = useState<GridScope>(scopeParam ? decodeScope(scopeParam) : { kind: 'base' });
  const viewParam = searchParams.get('view') as PriceListsView | null;
  const [priceListsView, setPriceListsView] = useState<PriceListsView>(
    viewParam && PRICE_LISTS_VIEWS.includes(viewParam) ? viewParam : 'lists',
  );
  const bulkViewParam = searchParams.get('view') as BulkChangesView | null;
  // The "Reprice into list" entry point (design spec §8.3) and the card drawer's "Open batch"
  // entry point (`?batch=`, design spec §12.4) both always target the batches sub-view.
  const initialTargetListCode = searchParams.get('targetListCode');
  const seedBatchId = searchParams.get('batch');
  const [bulkView, setBulkView] = useState<BulkChangesView>(
    initialTargetListCode || seedBatchId
      ? 'batches'
      : bulkViewParam && BULK_VIEWS.includes(bulkViewParam)
        ? bulkViewParam
        : 'batches',
  );
  // Derived, not local state: this page has no internal trigger that changes the seeded store
  // (unlike `ChannelStockPage`'s `openOverridesFor`) — the only way `?store=` changes is an
  // external deep link (the Evotor panel), which mounts this page fresh, so reading it straight
  // from `searchParams` on every render is simpler and behaviourally identical. `?group=`
  // (the grid's customer-group select, design spec §12.4) and `?productId=` (the card drawer's
  // approvals/audit deep links, §8.3/§8.7) are the same kind of read-on-mount seed — each panel
  // keeps owning its own local state, the page only feeds the initial value and echoes the param
  // back while its tab is active, exactly like `?store=`/`?targetListCode=` already do.
  const seedStore = searchParams.get('store');
  const seedGroupCode = searchParams.get('group');
  const seedProductId = searchParams.get('productId');

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (tab === 'priceLists') {
      next.set('view', priceListsView);
      if (seedStore) next.set('store', seedStore);
      else next.delete('store');
      next.delete('scope');
      next.delete('group');
      next.delete('productId');
      next.delete('batch');
    } else if (tab === 'bulk') {
      next.set('view', bulkView);
      next.delete('store');
      next.delete('scope');
      next.delete('group');
      next.delete('productId');
      if (seedBatchId) next.set('batch', seedBatchId);
      else next.delete('batch');
    } else if (tab === 'grid') {
      next.delete('view');
      next.delete('store');
      next.delete('productId');
      next.delete('batch');
      // `base` is the default scope and stays unwritten (design spec §5/§12) — only a non-base
      // selection earns a `?scope=` entry, mirroring `encodeScope`'s own `base` sentinel.
      const encodedScope = encodeScope(gridScope);
      if (encodedScope === BASE_SCOPE) next.delete('scope');
      else next.set('scope', encodedScope);
      if (seedGroupCode) next.set('group', seedGroupCode);
      else next.delete('group');
    } else if (tab === 'approvals' || tab === 'audit') {
      next.delete('view');
      next.delete('store');
      next.delete('scope');
      next.delete('group');
      next.delete('batch');
      if (seedProductId) next.set('productId', seedProductId);
      else next.delete('productId');
    } else {
      next.delete('view');
      next.delete('store');
      next.delete('scope');
      next.delete('group');
      next.delete('productId');
      next.delete('batch');
    }
    if (tab !== 'bulk') next.delete('targetListCode');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, priceListsView, bulkView, gridScope, seedStore, seedGroupCode, seedProductId, seedBatchId]);

  const onResult = (severity: 'success' | 'error' | 'info' | 'warning', message: string) =>
    severity === 'success' ? success(message) : notifyError(message);

  // Drops `?productId=` — the "Product: {id}" filter chip's dismiss action on both the Approvals
  // and the Audit tab (design spec §8.3/§8.7); the tab-sync effect above then stops re-adding it.
  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    setSearchParams(next, { replace: true });
  };

  if (!canPricingView) {
    return (
      <PageContainer title={t('pricing.title')}>
        <ErrorState
          title={t('pricing.accessRestricted.title')}
          message={t('pricing.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={t('pricing.title')}
      subtitle={t('pricing.subtitle')}
      actions={
        <Tooltip title={t('pricing.settingsTooltip')}>
          <IconButton
            component={RouterLink}
            to="/settings/pricing"
            aria-label={t('pricing.settingsTooltip')}
            sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}
          >
            <SettingsRoundedIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_e, value: TabKey) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="grid" label={t('pricing.tabs.grid')} />
          <Tab value="priceLists" label={t('pricing.tabs.priceLists')} />
          <Tab value="bulk" label={t('pricing.tabs.bulk')} />
          <Tab value="rules" label={t('pricing.tabs.rules')} />
          <Tab value="config" label={t('pricing.tabs.config')} />
          <Tab value="approvals" label={t('pricing.tabs.approvals')} />
          <Tab value="protection" label={t('pricing.tabs.protection')} />
          <Tab value="audit" label={t('pricing.tabs.audit')} />
          <Tab value="channels" label={t('pricing.tabs.channels')} />
          <Tab value="operations" label={t('pricing.tabs.operations')} />
        </Tabs>
      </Box>

      {tab === 'grid' ? (
        <PriceGridPanel scope={gridScope} onScopeChange={setGridScope} initialGroupCode={seedGroupCode} />
      ) : null}
      {tab === 'priceLists' ? (
        <PriceListsPanel
          view={priceListsView}
          onViewChange={setPriceListsView}
          seedStore={seedStore}
          canManage={canPricingManage}
          canAdmin={canPricingAdmin}
          onResult={onResult}
        />
      ) : null}
      {tab === 'bulk' ? (
        <BulkChangesPanel
          view={bulkView}
          onViewChange={setBulkView}
          canManage={canPricingManage}
          canBulk={canPricingBulk}
          initialTargetListCode={initialTargetListCode ?? undefined}
          initialBatchId={seedBatchId ?? undefined}
          onResult={onResult}
        />
      ) : null}
      {tab === 'rules' ? <PricingRulesPanel canManage={canPricingManage} onResult={onResult} /> : null}
      {tab === 'config' ? <PricingConfigPanel canManage={canPricingManage} onResult={onResult} /> : null}
      {tab === 'approvals' ? (
        <PriceApprovalsPanel
          canManage={canPricingManage}
          onResult={onResult}
          productId={seedProductId}
          onClearProductFilter={clearProductFilter}
        />
      ) : null}
      {tab === 'protection' ? (
        <PriceProtectionPanel canManage={canPricingManage} onResult={onResult} />
      ) : null}
      {tab === 'audit' ? (
        <PriceAuditPanel productId={seedProductId} onClearProductFilter={clearProductFilter} />
      ) : null}
      {tab === 'channels' ? <ChannelsPanel /> : null}
      {tab === 'operations' ? (
        <PriceOperationsPanel canManage={canPricingManage} onResult={onResult} />
      ) : null}

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
